/**
 * Reads the `reset` token out of a login URL.
 *
 * The token is the raw Base64 output of `CryptoJS.AES.encrypt(...)`, so it can
 * contain `+`, `/` and `=`. The server mints links with the token RAW and
 * compares it on redemption against `security.temp`, which holds those same
 * bytes — so the job here is to hand back exactly what was in the URL.
 *
 * It must nonetheless accept BOTH forms:
 *
 *   RAW      `?reset=abc+def/ghi=`       → `abc+def/ghi=`   (what we mint)
 *   ENCODED  `?reset=abc%2Bdef%2Fghi%3D` → `abc+def/ghi=`   (2026-07-07 → revert)
 *
 * For seven weeks the welcome path percent-encoded the token while this app
 * still read it with a naive `href.split("?reset=")[1]`. The server received
 * `U2FsdGVkX1%2B…`, which is not valid Base64: the decrypt yielded garbage or
 * threw, and the token never matched `security.temp`. Those links are still in
 * patients' inboxes and `security.temp` has no expiry, so they must keep
 * working. On a raw token the decode below is the identity function.
 *
 * A literal `+` is protected as `%2B` before decoding so it survives as a plus
 * sign rather than being read as an encoded space.
 *
 * Mirrors `blhClinicApp/src/utils/resetToken.js`; the emission side is
 * `blhWebServer/src/utils/resetLink.js`, which explains why we mint raw.
 *
 * @param {string} [href] the URL to read; defaults to the current location
 * @returns {string} the raw token, or "" when the URL carries none
 */
export function getResetTokenFromHref(href = window.location.href) {
  const queryStart = href.indexOf("?");
  if (queryStart === -1) return "";

  const resetParam = href
    .slice(queryStart + 1)
    .split("&")
    .find((part) => part.startsWith("reset="));

  if (!resetParam) return "";

  const rawToken = resetParam.slice("reset=".length);
  try {
    return decodeURIComponent(rawToken.replace(/\+/g, "%2B"));
  } catch {
    return rawToken;
  }
}
